import React from 'react'

interface SubmittedPriceInfoProps {
  price?: number
  deliveryTime?: number
  comment?: string
  priceFormatted?: string
  deliveryTimeFormatted?: string
  status?: string
  // Информация о дедлайне
  deadlineFormatted?: string
  deadlineStatus?: string
  deadlineCssClass?: string
  daysRemaining?: number
}

export const SubmittedPriceInfo: React.FC<SubmittedPriceInfoProps> = ({
  price,
  deliveryTime,
  comment,
  priceFormatted,
  deliveryTimeFormatted,
  status,
  // Информация о дедлайне
  deadlineFormatted,
  deadlineStatus,
  deadlineCssClass,
  daysRemaining
}) => {
  // Определяем заголовок в зависимости от статуса
  const getTitle = () => {
    if (status === 'executing' || status === 'defect' || status === 'completed' || status === 'rejected') {
      return 'Принял условия'
    }
    return 'Цена предложена'
  }

  // Определяем стили в зависимости от статуса
  const getCardStyles = () => {
    const isAccepted = status === 'executing' || status === 'defect' || status === 'completed' || status === 'rejected'
    
    if (isAccepted) {
      return {
        cardClass: 'bg-blue-50 border-blue-200',
        iconClass: 'text-blue-700',
        icon: '✅',
        borderClass: 'border-blue-200'
      }
    }
    
    return {
      cardClass: 'bg-green-50 border-green-200',
      iconClass: 'text-green-700', 
      icon: '💰',
      borderClass: 'border-green-200'
    }
  }

  // Форматируем цену если не передана готовая строка
  const displayPrice = priceFormatted || (price ? `${price.toLocaleString('ru-RU')} руб.` : null)
  
  // Форматируем срок если не передана готовая строка
  const displayDeliveryTime = deliveryTimeFormatted || (deliveryTime ? 
    `${deliveryTime} рабочий${deliveryTime === 1 ? ' день' : deliveryTime < 5 ? ' дня' : ' дней'}` : null)

  // Функция для определения цвета текста дедлайна
  const getDeadlineTextColor = () => {
    if (!daysRemaining && daysRemaining !== 0) return 'text-gray-800'
    
    if (daysRemaining < 0) return 'text-red-600'      // Просрочено
    if (daysRemaining === 0) return 'text-orange-600' // Сегодня
    if (daysRemaining <= 3) return 'text-red-500'     // Срочно
    if (daysRemaining <= 7) return 'text-yellow-600'  // Предупреждение
    return 'text-green-600'                           // Нормально
  }

  // Проверяем нужно ли показывать дедлайн
  // Показываем дедлайн для статусов с принятыми условиями или когда есть дедлайн
  const shouldShowDeadline = deadlineFormatted && (
    status === 'executing' || 
    status === 'defect' || 
    status === 'completed' || 
    status === 'rejected' ||
    (status === 'pending_confirmation' && deadlineFormatted) // Добавляем поддержку pending_confirmation с дедлайном
  )

  if (!displayPrice && !displayDeliveryTime) {
    return null
  }

  const cardStyles = getCardStyles()

  return (
    <div className={`${cardStyles.cardClass} rounded-xl shadow-sm p-3 sm:p-4 mb-4 border transition-all duration-200 hover:shadow-md`}>
      <div className="flex items-center mb-3">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
          <span className={`mr-2 text-lg sm:text-xl ${cardStyles.iconClass}`}>{cardStyles.icon}</span>
          {getTitle()}
        </h3>
      </div>

      <div className="space-y-2 text-sm sm:text-base text-gray-700">
        {displayPrice && (
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-600">Цена:</span>
            <span className="font-bold text-gray-900">{displayPrice}</span>
          </div>
        )}
        
        {displayDeliveryTime && (
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-600">Срок:</span>
            <span className="text-gray-800">{displayDeliveryTime}</span>
          </div>
        )}
        
        {/* Отображение дедлайна для принятых условий */}
        {shouldShowDeadline && (
          <div className={`flex justify-between items-center ${(status === 'completed' || status === 'rejected') ? 'line-through text-gray-400' : ''}`}>
            <span className="font-medium text-gray-600">
              Сдать до:
            </span>
            <span className={`font-semibold ${(status === 'completed' || status === 'rejected') ? 'text-gray-400' : getDeadlineTextColor()}`}>
              {deadlineFormatted}
              {deadlineStatus && deadlineStatus !== deadlineFormatted && (
                <span className="ml-1 text-xs">({deadlineStatus})</span>
              )}
            </span>
          </div>
        )}
      </div>

      {comment && (
        <div className={`mt-3 pt-3 border-t ${cardStyles.borderClass}`}>
          <p className="font-medium text-gray-600 text-xs sm:text-sm mb-1">Комментарий:</p>
          <p className="text-gray-700 text-xs sm:text-sm italic">{comment}</p>
        </div>
      )}
    </div>
  )
}

export default SubmittedPriceInfo
